import config from 'config';

import {
  getCioSortOrder,
  getPreFilterExpressions,
  getSanitizedFilterInputsForCio,
  isolateSizeFilters,
} from '~/grid/utils/cioUtils';

import { SEARCH_API_PAGE_SIZE } from '../constants';

/**
 * @function getCioSearchResults
 * @description Fetches search results from Constructor.io's search API
 * @param {Object} cioClient - Constructor.io client instance
 * @param {String} searchQuery - Search query string
 * @param {Boolean} isElite - Is the user an elite member
 * @param {Boolean} isVip - Is the user a VIP member
 * @param {Number} page - Page number for pagination
 * @param {Array} selectedUrlFilters - Array of selected filters from the URL
 * @param {String} selectedSortOption - Selected sort option
 * @param {String} tld - Top-level domain
 * @param {Boolean} removeOriginFlag - Flag to remove origin restricted products
 * @param {Boolean} isSecondarySite - Flag to indicate if the site is secondary
 * @param {Boolean} surfaceHiddenFacets - Flag to indicate if hidden facets should be surfaced
 * @param {Array} excludeTagIds - Array of tag ids to exclude
 * @param {Array} excludeFplCodes - Array of FPL global codes to exclude
 * @param {Object} hiddenFacetFilters - Object containing hidden facet filters
 * @return {Object} Search results
 */
const getCioSearchResults = async ({
  cioClient,
  searchQuery,
  isElite = false,
  isVip = false,
  page = 1,
  selectedUrlFilters,
  selectedSortOption,
  tld = 'com',
  removeOriginFlag = false,
  isSecondarySite = false,
  surfaceHiddenFacets = false,
  excludeTagIds = [],
  excludeFplCodes = [],
  hiddenFacetFilters = {},
}) => {
  const staticCurrentDate = new Date();

  const { sizeFilters, nonSizeFilters } =
    isolateSizeFilters(selectedUrlFilters);
  const hiddenFacets = config.get('public.brand.constructor.hiddenFacets');

  const sanitizedFilters = getSanitizedFilterInputsForCio(nonSizeFilters);

  const filters = {
    ...sanitizedFilters,
    ...hiddenFacetFilters,
  };

  const searchParameters = {
    resultsPerPage: SEARCH_API_PAGE_SIZE,
    page,
    preFilterExpression: getPreFilterExpressions({
      isElite,
      isVip,
      dateNowInMs: staticCurrentDate.getTime(),
      shouldShowWarehouseProducts: isSecondarySite,
      shouldHideOriginRestricted: removeOriginFlag,
      selectedSizeFilters: sizeFilters,
      tld,
      isSearch: true,
      excludeTagIds,
      excludeFplCodes,
    }),
    filters,
    sortBy: selectedSortOption,
    sortOrder: getCioSortOrder(selectedSortOption),
  };

  if (surfaceHiddenFacets && hiddenFacets?.length) {
    searchParameters.hiddenFacets = hiddenFacets;
  }

  const results = await cioClient.search.getSearchResults(
    searchQuery,
    searchParameters
  );

  const hasNextPage =
    page * SEARCH_API_PAGE_SIZE < results?.response?.total_num_results;
  const nextPage = hasNextPage ? page + 1 : null;
  const totalNumResults = results?.response?.total_num_results;
  const redirectUrl = results?.response?.redirect?.data?.url;

  return { ...results, nextPage, totalNumResults, redirectUrl };
};

export default getCioSearchResults;
